import React from "react";
import type { BikeImpressions } from "../../types/impressionJob";

interface JobAnalyticsChartsProps {
  analytics: BikeImpressions[];
}

export const JobAnalyticsCharts: React.FC<JobAnalyticsChartsProps> = ({
  analytics,
}) => {
  // Aggregate daily impressions across all bikes
  const dailyTotals = analytics.reduce((acc, bike) => {
    bike.daily_impressions.forEach((day) => {
      acc[day.date] = (acc[day.date] || 0) + day.total_impressions;
    });
    return acc;
  }, {} as Record<string, number>);

  const sortedDates = Object.keys(dailyTotals).sort();
  const maxImpressions = Math.max(...Object.values(dailyTotals));

  // Aggregate hourly impressions across all bikes and days
  const hourlyTotals: Record<string, number> = {};
  analytics.forEach((bike) => {
    bike.daily_impressions.forEach((day) => {
      day.hourly_impressions.forEach((hour) => {
        const hourKey = hour.hour.split("-")[0]; // Get "HH:00" part
        hourlyTotals[hourKey] = (hourlyTotals[hourKey] || 0) + hour.impressions;
      });
    });
  });

  const hours = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, "0")}:00`);
  const maxHourlyImpressions = Math.max(...hours.map((h) => hourlyTotals[h] || 0));

  // Per-bike totals
  const bikeTotals = analytics.map((bike) => ({
    bike_id: bike.bike_id,
    total: bike.daily_impressions.reduce((sum, day) => sum + day.total_impressions, 0),
  })).sort((a, b) => b.total - a.total);

  const maxBikeImpressions = bikeTotals[0]?.total || 1;

  return (
    <div className="space-y-8">
      {/* Daily Impressions Chart */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Daily Impressions Trend
        </h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="space-y-2">
            {sortedDates.map((date) => {
              const impressions = dailyTotals[date];
              const percentage = (impressions / maxImpressions) * 100;
              return (
                <div key={date} className="flex items-center gap-3">
                  <div className="w-24 text-sm text-gray-600 flex-shrink-0">
                    {new Date(date).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-full h-8 relative">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full flex items-center justify-end px-3 transition-all duration-300"
                      style={{ width: `${Math.max(percentage, 5)}%` }}
                    >
                      <span className="text-white text-sm font-medium">
                        {impressions.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Hourly Distribution Chart */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Hourly Impressions Distribution
        </h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-end gap-1 h-64">
            {hours.map((hour) => {
              const impressions = hourlyTotals[hour] || 0;
              const percentage = maxHourlyImpressions > 0 ? (impressions / maxHourlyImpressions) * 100 : 0;
              return (
                <div key={hour} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex items-end justify-center" style={{ height: "200px" }}>
                    {impressions > 0 && (
                      <div
                        className="w-full bg-gradient-to-t from-green-500 to-green-400 rounded-t hover:from-green-600 hover:to-green-500 transition-all cursor-pointer relative group"
                        style={{ height: `${Math.max(percentage, 5)}%` }}
                        title={`${hour}: ${impressions.toLocaleString()} impressions`}
                      >
                        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                          {impressions.toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-600 transform -rotate-45 origin-top-left mt-2">
                    {hour.split(":")[0]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Per-Bike Impressions Chart */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Impressions by Bike
        </h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="space-y-2">
            {bikeTotals.map((bike, index) => {
              const percentage = (bike.total / maxBikeImpressions) * 100;
              const colorClasses = [
                "from-purple-500 to-purple-600",
                "from-indigo-500 to-indigo-600",
                "from-blue-500 to-blue-600",
                "from-cyan-500 to-cyan-600",
                "from-teal-500 to-teal-600",
              ];
              const colorClass = colorClasses[index % colorClasses.length];

              return (
                <div key={bike.bike_id} className="flex items-center gap-3">
                  <div className="w-20 text-sm text-gray-600 flex-shrink-0 font-mono">
                    {bike.bike_id}
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-full h-8 relative">
                    <div
                      className={`bg-gradient-to-r ${colorClass} h-full rounded-full flex items-center justify-end px-3 transition-all duration-300`}
                      style={{ width: `${Math.max(percentage, 5)}%` }}
                    >
                      <span className="text-white text-sm font-medium">
                        {bike.total.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
